import React from 'react';
import PropTypes from 'prop-types';

/**
 * Form Components
 * 
 * Standardized form inputs with consistent styling and dark mode support.
 */

/**
 * TextInput Component
 * 
 * Standard text input with label and error support.
 * Replaces: <input className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded...">
 * 
 * Usage:
 * <TextInput 
 *   label="Email" 
 *   value={email} 
 *   onChange={setEmail} 
 *   placeholder="email@example.com"
 * />
 */
export const TextInput = ({
  type = 'text',
  value = '',
  onChange,
  label = '',
  placeholder = '',
  error = '',
  disabled = false,
  required = false,
  mono = false,
  size = 'md',
  className = '',
  ...props
}) => {
  const sizeClasses = {
    sm: 'p-1.5 text-sm',
    md: 'p-2',
    lg: 'p-3 text-lg'
  };

  const baseClasses = `w-full border rounded theme-transition focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
    bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
    border-gray-300 dark:border-gray-600
    placeholder-gray-500 dark:placeholder-gray-400
    disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed`;

  const monoClass = mono ? 'font-mono text-sm' : '';
  const errorClass = error ? 'border-danger-500 focus:ring-danger-500' : '';

  return (
    <div className={`${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
          {required && <span className="text-danger-500 ml-1">*</span>}
        </label>
      )}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={`${baseClasses} ${sizeClasses[size]} ${monoClass} ${errorClass}`}
        {...props}
      />
      {error && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{error}</p>}
    </div>
  );
};

TextInput.propTypes = {
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  mono: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string
};

/**
 * TextArea Component
 * 
 * Multi-line text input.
 * 
 * Usage:
 * <TextArea 
 *   label="Description" 
 *   value={description} 
 *   onChange={setDescription}
 *   rows={4}
 * />
 */
export const TextArea = ({
  value = '',
  onChange,
  label = '',
  placeholder = '',
  error = '',
  disabled = false,
  required = false,
  mono = false,
  rows = 4,
  resize = 'vertical', // 'none', 'vertical', 'horizontal', 'both'
  className = '',
  ...props
}) => {
  const baseClasses = `w-full p-2 border rounded theme-transition focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
    bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
    border-gray-300 dark:border-gray-600
    placeholder-gray-500 dark:placeholder-gray-400
    disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed`;

  const monoClass = mono ? 'font-mono text-sm' : '';
  const errorClass = error ? 'border-danger-500 focus:ring-danger-500' : '';
  
  const resizeClasses = {
    none: 'resize-none',
    vertical: 'resize-y',
    horizontal: 'resize-x',
    both: 'resize'
  };

  return (
    <div className={`${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
          {required && <span className="text-danger-500 ml-1">*</span>}
        </label>
      )}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        rows={rows}
        className={`${baseClasses} ${monoClass} ${errorClass} ${resizeClasses[resize]}`}
        {...props}
      />
      {error && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{error}</p>}
    </div>
  );
};

TextArea.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  mono: PropTypes.bool,
  rows: PropTypes.number,
  resize: PropTypes.oneOf(['none', 'vertical', 'horizontal', 'both']),
  className: PropTypes.string
};

/**
 * Select Component
 * 
 * Dropdown select input.
 * 
 * Usage:
 * <Select 
 *   label="Provider" 
 *   value={provider} 
 *   onChange={setProvider}
 *   options={[
 *     { value: 'openai', label: 'OpenAI' },
 *     { value: 'anthropic', label: 'Anthropic' }
 *   ]}
 * />
 */
export const Select = ({
  value = '',
  onChange,
  label = '',
  options = [],
  placeholder = 'Bitte wählen...',
  error = '',
  disabled = false,
  required = false,
  size = 'md',
  className = '',
  ...props
}) => {
  const sizeClasses = {
    sm: 'p-1.5 text-sm',
    md: 'p-2',
    lg: 'p-3 text-lg'
  };

  const baseClasses = `w-full border rounded theme-transition focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
    bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
    border-gray-300 dark:border-gray-600
    disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed`;

  const errorClass = error ? 'border-danger-500 focus:ring-danger-500' : '';

  return (
    <div className={`${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
          {required && <span className="text-danger-500 ml-1">*</span>}
        </label>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required={required}
        className={`${baseClasses} ${sizeClasses[size]} ${errorClass}`}
        {...props}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{error}</p>}
    </div>
  );
};

Select.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })).isRequired,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string
};

/**
 * NumberInput Component
 * 
 * Number input with optional range slider.
 * 
 * Usage:
 * <NumberInput 
 *   label="Temperature" 
 *   value={temp} 
 *   onChange={setTemp}
 *   min={0}
 *   max={1}
 *   step={0.1}
 *   showSlider
 * />
 */
export const NumberInput = ({
  value = 0,
  onChange,
  label = '',
  min = 0,
  max = 100,
  step = 1,
  error = '',
  disabled = false,
  required = false,
  showSlider = false,
  className = '',
  ...props
}) => {
  const baseClasses = `w-full p-2 border rounded theme-transition focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
    bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
    border-gray-300 dark:border-gray-600
    disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed`;

  const errorClass = error ? 'border-danger-500 focus:ring-danger-500' : '';

  return (
    <div className={`${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
          {required && <span className="text-danger-500 ml-1">*</span>}
          <span className="ml-2 text-gray-500 dark:text-gray-400 font-normal">({value})</span>
        </label>
      )}
      {showSlider ? (
        <input
          type="range"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary-500"
          {...props}
        />
      ) : (
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          required={required}
          className={`${baseClasses} ${errorClass}`}
          {...props}
        />
      )}
      {error && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{error}</p>}
    </div>
  );
};

NumberInput.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  showSlider: PropTypes.bool,
  className: PropTypes.string
};

/**
 * FormField Component
 * 
 * Wrapper for form fields with label, description, and error.
 * 
 * Usage:
 * <FormField label="Name" description="Your display name" error={errors.name}>
 *   <TextInput ... />
 * </FormField>
 */
export const FormField = ({
  label = '',
  description = '',
  error = '',
  required = false,
  children,
  className = ''
}) => {
  return (
    <div className={`${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
          {required && <span className="text-danger-500 ml-1">*</span>}
        </label>
      )}
      {description && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{description}</p>
      )}
      {children}
      {error && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{error}</p>}
    </div>
  );
};

FormField.propTypes = {
  label: PropTypes.string,
  description: PropTypes.string,
  error: PropTypes.string,
  required: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string
};

/**
 * Checkbox Component
 * 
 * Checkbox input with label.
 * 
 * Usage:
 * <Checkbox 
 *   label="Enable notifications" 
 *   checked={enabled} 
 *   onChange={setEnabled}
 * />
 */
export const Checkbox = ({
  checked = false,
  onChange,
  label = '',
  description = '',
  disabled = false,
  className = ''
}) => {
  return (
    <label className={`flex items-start gap-3 cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => !disabled && onChange(e.target.checked)}
        disabled={disabled}
        className="mt-1 w-4 h-4 text-primary-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 focus:ring-2"
      />
      <div>
        {label && <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>}
        {description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>}
      </div>
    </label>
  );
};

Checkbox.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  description: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string
};

export default {
  TextInput,
  TextArea,
  Select,
  NumberInput,
  FormField,
  Checkbox
};
